import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap, catchError, throwError, tap } from 'rxjs';
import { API_ENDPOINTS } from '../api/api-endpoints';
import { appendTalkdownAttempt } from './talkdown-attempts-storage';
import { mapDealerEventsToQueue } from '../api/dealer/dealer-event.mapper';
import {
  mapDealerInProgressToAgentOpenWork,
  mapDealerInProgressToEventsInProcess,
  mapDealerInProgressToPatrolList,
} from '../api/dealer/dealer-in-process.mapper';
import {
  mapCompletePatrolSessionResponse,
  mapDealerStartedPatrolEvent,
  resolvePatrolSessionEventId,
} from '../api/dealer/dealer-patrol.mapper';
import { mapDealerClaimToEventHandle } from '../api/dealer/dealer-event-handle.mapper';
import {
  mapDealerParkedEventsToResponse,
  mapDealerParkEventToResponse,
} from '../api/dealer/dealer-parked.mapper';
import {
  requireIsoDateTime,
  requireNonBlankEventId,
  requirePositiveSiteId,
} from '../api/resolve/resolve-request.util';
import {
  mapDealerAlarmsToEventAlarms,
  mergeNewEventAlarms,
  replaceFlaggedStateOnAlarms,
} from '../api/dealer/dealer-alarm.mapper';
import {
  mapDealerDevicesToCameras,
  mapDealerDevicesToTalkdowns,
} from '../api/dealer/dealer-device.mapper';
import {
  emptyHistoricPlaybackAlarmSegments,
  mapHistoricAlarmsToTimelineSegments,
  mapSiteEventHistoryToEventIds,
  mergeHistoricEventIds,
  type HistoricPlaybackAlarmsResult,
} from '../api/resolve/historic-alarm-timeline.mapper';
import {
  mapDealerActivityTypesToCloseOutcomes,
  mapDealerActivityTypesToRaiseOutcomes,
} from '../api/dealer/dealer-activity-type.mapper';
import { mapCameraReportReasonToDealerCaseName } from '../api/dealer/dealer-case.mapper';
import {
  mapDealerTypeValuesToDisarmReasons,
  mapDisarmSiteRequestToDealer,
} from '../api/dealer/dealer-disarm.mapper';
import {
  mapResolveAssetToClipResponse,
  mapResolveAssetToSnapshotResponse,
} from '../api/resolve/resolve-event-asset.mapper';
import {
  mapDealerSitesToLocations,
  mapDealerSiteSummariesToLocations,
  resolveDealerAllowedSiteIds,
} from '../api/dealer/dealer-site.mapper';
import {
  buildPlaybackClipFilename,
  parseContentDispositionFilename,
} from '../utils/playback-clip.util';
import {
  AiFeedbackRequest,
  AiFeedbackResponse,
  doriAnnotationReasonFromVariant,
  DoriAuthConfigError,
  DoriFlagInferenceRequest,
  DoriFlagInferenceResponse,
} from '../models/entities/ai-feedback.model';
import { ApiRuntimeConfig } from './api-runtime-config';
import { CameraReportRequest, CameraReportResponse } from '../models/entities/camera-report.model';
import { CameraPtzRequest, CameraPtzResponse } from '../models/entities/camera-ptz.model';
import {
  CloseEventRequest,
  EventActionResponse,
  RaiseToResolveRequest,
} from '../models/entities/event-action.model';
import { SnapshotRequest, SnapshotResponse } from '../models/entities/snapshot.model';
import {
  AttachAlarmClipRequest,
  AttachAlarmClipResponse,
} from '../models/entities/alarm-clip.model';
import { getOrCreateBrowserSessionId } from '../auth/shared-session.storage';
import { AuthLoginResponse, SignalRNegotiateResponse } from '../models/entities/auth-login.model';
import { BrowserTabDefaults } from '../models/entities/browser-tab-defaults.model';
import {
  EventAlarmsResponse,
  EventCamerasResponse,
  EventDetail,
  EventTalkdownsResponse,
} from '../models/entities/event-detail.model';
import {
  CameraDetectionsResponse,
  EventCameraStreamResponse,
  EventPlaybackClipDownload,
  EventPlaybackClipRequest,
} from '../models/entities/event-playback.model';
import {
  AgentOpenWorkResponse,
  EventsInProcessResponse,
} from '../models/entities/events-in-process.model';
import {
  ClaimEventHandleRequest,
  EventHandle,
  EventHandlesResponse,
  ReleaseEventHandleRequest,
} from '../models/entities/event-handle.model';
import { EventOutcomesResponse } from '../models/entities/event-outcomes.model';
import { EventsMockSettings } from '../models/entities/events-mock-settings.model';
import {
  EventsParkedResponse,
  ParkEventRequest,
  ParkEventResponse,
} from '../models/entities/events-parked.model';
import { EventsQueueResponse } from '../models/entities/events-queue.model';
import {
  ResolveCameraAlarmHistoryResponse,
  ResolveCreateIncidentResponse,
  ResolveEventsResponse,
  ResolveGetCameraAlarmHistoryResponse,
  ResolveGetEventsResponse,
  ResolveGetSiteContactsResponse,
  ResolveGetSiteEventHistoryResponse,
  ResolveSaveEventAssetResponse,
  ResolveSiteContactsResponse,
} from '../models/entities/resolve-api.model';
import {
  DealerActivateTalkdownRequest,
  DealerActivityType,
  DealerClaimEventRequest,
  DealerClaimEventResponse,
  DealerCloseEventResponse,
  DealerCreateCaseResponse,
  DealerGetActivityTypesResponse,
  DealerEscalateEventResponse,
  DealerFlagEventAlarm,
  DealerFlagEventAlarmsResponse,
  DealerGetAllowedSitesResponse,
  DealerGetArmedSitesResponse,
  DealerGetDisarmedSitesResponse,
  DealerGetEventsResponse,
  DealerDevice,
  DealerGetInProgressEventsResponse,
  DealerGetParkedEventsResponse,
  DealerParkEventResponse,
  DealerGetSiteDevicesResponse,
  DealerAlarmStatus,
  DealerGetEventAlarmsResponse,
  DealerGetFlaggedEventAlarmsResponse,
  DealerGetTypeValuesResponse,
  DealerPaginationResponseOfSiteItem,
  DealerStartPatrolResponse,
} from '../models/entities/dealer-api.model';
import { LoginSessionDefaults } from '../models/entities/login-session-defaults.model';
import { PatrolListResponse } from '../models/entities/patrol-list.model';
import {
  CompletePatrolSessionRequest,
  CompletePatrolSessionResponse,
  PatrolSessionType,
  StartPatrolSessionRequest,
  StartPatrolSessionResponse,
} from '../models/entities/patrol-session.model';
import { SiteLocationsResponse } from '../models/entities/site-location.model';
import { SiteDisarmReasonsResponse } from '../models/entities/site-disarm-reasons.model';
import {
  DisarmSiteRequest,
  DisarmSiteResponse,
} from '../models/entities/site-arm-disarm.model';
import {
  DisarmCameraRequest,
  DisarmCameraResponse,
} from '../models/entities/camera-arm-disarm.model';
import { SiteStats } from '../models/entities/site-stats.model';
import { EVENTQ_SORT_QUERY_KEYS, EventqSortParams } from '../models/entities/sort-params.model';
import {
  mapAlarms,
  mapEventDetail,
  mapInProcess,
  mapDealerSiteList,
  mapQueue,
} from '../api/dto-mappers';
import {
  mapCameraPtzActionToDealer,
  requirePositiveCameraId,
} from '../api/dealer/dealer-ptz.mapper';

/** Mirrors `BrowserTabService`'s own fallbacks for when no mock server supplies defaults. */
const FALLBACK_BROWSER_TAB_DEFAULTS: BrowserTabDefaults = {
  defaultTabLabel: 'New Event',
  defaultTabs: [{ id: 't1', label: 'New Event' }],
};

/**
 * Outcome ids come from Dealer's activity-type catalog, which keys on int32.
 * Anything non-numeric is passed through so the backend can reject it plainly
 * rather than the UI silently sending NaN.
 */
function activityTypeId(outcomeId: string): number | string {
  const parsed = Number(outcomeId);
  return Number.isInteger(parsed) && outcomeId.trim() !== '' ? parsed : outcomeId;
}

/**
 * Typed HTTP accessors for agent UI data. Paths are `/api/dealer` and `/api/auth`
 * against the mock (or production) backend.
 */
@Injectable({ providedIn: 'root' })
export class AgentDataService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiRuntimeConfig);

  login(email: string, password: string): Observable<AuthLoginResponse> {
    return this.http.post<AuthLoginResponse>(API_ENDPOINTS.authLogin.apiPath, {
      email,
      password,
      browserSessionId: getOrCreateBrowserSessionId(),
    });
  }

  signalrNegotiate(): Observable<SignalRNegotiateResponse> {
    return this.http.get<SignalRNegotiateResponse>(API_ENDPOINTS.signalrNegotiate.apiPath);
  }

  getEventsQueue(): Observable<EventsQueueResponse> {
    return this.http
      .get<DealerGetEventsResponse>(this.dealerUrl('/events'), {
        params: { Top: String(this.apiConfig.dealerEventsTop()) },
      })
      .pipe(map((response) => mapDealerEventsToQueue(response.items ?? [])));
  }

  /** Patrols and site-checks are in-progress events of type 1 / 2. */
  getPatrolList(): Observable<PatrolListResponse> {
    return this.http
      .get<DealerGetInProgressEventsResponse>(
        this.dealerUrl('/events/in-progress'),
      )
      .pipe(map((response) => mapDealerInProgressToPatrolList(response.items)));
  }

  /** Starts a manual patrol for a site. */
  startManualPatrol(body: StartPatrolSessionRequest): Observable<StartPatrolSessionResponse> {
    return this.startPatrolEvent(body, 'manual-patrol');
  }

  /** Starts a site-check for a site. */
  startSiteCheck(body: StartPatrolSessionRequest): Observable<StartPatrolSessionResponse> {
    return this.startPatrolEvent(body, 'site-check');
  }

  private startPatrolEvent(
    body: StartPatrolSessionRequest,
    type: PatrolSessionType,
  ): Observable<StartPatrolSessionResponse> {
    const action = type === 'site-check' ? 'start-site-check' : 'start-patrol';
    return this.http
      .post<DealerStartPatrolResponse>(
        this.dealerUrl(`/events/${encodeURIComponent(body.siteId)}/${action}`),
        null,
      )
      .pipe(map((response) => mapDealerStartedPatrolEvent(response.event, type, body.siteName)));
  }

  /**
   * Finishes a patrol or site-check. Dealer models these as ordinary events, so
   * completion is the same close / raise call the alarm flow uses.
   */
  completePatrolSession(
    sessionId: string,
    body: CompletePatrolSessionRequest,
  ): Observable<CompletePatrolSessionResponse> {
    const eventId = resolvePatrolSessionEventId(sessionId);
    const completed =
      body.decision === 'raised'
        ? this.raiseEventToResolve(eventId, { outcomeId: body.outcomeId })
        : this.closeEvent(eventId, {
            outcomeId: body.outcomeId,
            closingDetails: body.closingDetails ?? '',
          });

    return completed.pipe(map(() => mapCompletePatrolSessionResponse(sessionId, eventId, body)));
  }

  getAgentOpenWork(): Observable<AgentOpenWorkResponse> {
    return this.http
      .get<DealerGetInProgressEventsResponse>(
        this.dealerUrl('/events/in-progress'),
      )
      .pipe(map((response) => mapDealerInProgressToAgentOpenWork(response.items)));
  }

  getEventsInProcess(): Observable<EventsInProcessResponse> {
    return this.http
      .get<DealerGetInProgressEventsResponse>(
        this.dealerUrl('/events/in-progress'),
      )
      .pipe(map((response) => mapDealerInProgressToEventsInProcess(response.items)));
  }

  /**
   * Active exclusive holds. Dealer records claims on the event itself rather than
   * as a separate resource, so outside mock mode the queue and `/events/claim`
   * own availability and there is nothing extra to fetch.
   */
  getEventHandles(): Observable<EventHandlesResponse> {
    if (!this.apiConfig.useMocks()) {
      return of({ items: [] });
    }
    return this.http.get<EventHandlesResponse>(this.dealerUrl(API_ENDPOINTS.eventHandles.apiPath));
  }

  /** Claims the next available Dealer queue event (empty body). */
  claimNextDealerEvent(
    body: Pick<ClaimEventHandleRequest, 'tabInstanceId' | 'agentEmail' | 'agentDisplayName'>,
  ): Observable<EventHandle> {
    return this.http
      .post<DealerClaimEventResponse | null>(
        this.dealerUrl('/events/claim'),
        {},
      )
      .pipe(
        map((response) =>
          mapDealerClaimToEventHandle(response, {
            ...body,
            eventId: response?.event?.eventId ?? '',
            location: '',
            firstAlarmDetected: '',
            priority: 0,
            alarmCount: 0,
          }),
        ),
      );
  }

  /**
   * Claims an event for a tab workspace. Server enforces uniqueness — concurrent
   * claims race in the DB and losers receive 409 EVENT_HELD.
   */
  claimEventHandle(
    body: ClaimEventHandleRequest & {
      location: string;
      firstAlarmDetected: string;
      priority: number;
      alarmCount: number;
    },
  ): Observable<EventHandle> {
    return this.http
      .post<DealerClaimEventResponse | null>(
        this.dealerUrl('/events/claim'),
        { eventId: body.eventId } satisfies DealerClaimEventRequest,
      )
      .pipe(map((response) => mapDealerClaimToEventHandle(response, body)));
  }

  /**
   * Dealer has no release operation — a claim is given up by closing or parking the
   * event, or by letting the lease lapse. Clearing the tab is all the UI can do.
   */
  releaseEventHandle(_body: ReleaseEventHandleRequest): Observable<{ success: true }> {
    return of({ success: true } as const);
  }

  /** Dealer publishes no stats route; the counts come from the two site lists. */
  getSiteStats(): Observable<SiteStats> {
    return forkJoin({
      armed: this.getArmedSites(),
      disarmed: this.getDisarmedSites(),
    }).pipe(
      map(({ armed, disarmed }) => ({
        armedCount: armed.items.length,
        disarmedCount: disarmed.items.length,
      })),
    );
  }

  /** Prefills the mock sign-in form; on a real gateway sign-in goes through Okta. */
  getLoginSessionDefaults(): Observable<LoginSessionDefaults> {
    if (!this.apiConfig.mockApiAvailable()) {
      return of({ defaultEmail: '', displayName: '' });
    }
    return this.http.get<LoginSessionDefaults>(API_ENDPOINTS.loginSessionDefaults.apiPath);
  }

  logout(): Observable<{ success: boolean }> {
    if (!this.apiConfig.mockApiAvailable()) {
      return of({ success: true });
    }
    return this.http.post<{ success: boolean }>('/api/auth/logout', {});
  }

  getBrowserTabDefaults(): Observable<BrowserTabDefaults> {
    if (!this.apiConfig.mockApiAvailable()) {
      return of(FALLBACK_BROWSER_TAB_DEFAULTS);
    }
    return this.http.get<BrowserTabDefaults>(API_ENDPOINTS.browserTabDefaults.apiPath);
  }

  getSiteLocations(search?: string, armed?: boolean): Observable<SiteLocationsResponse> {
    const trimmed = search?.trim();
    if (typeof armed === 'boolean') {
      const source = armed ? this.getArmedSites() : this.getDisarmedSites();
      if (!trimmed) {
        return source;
      }
      const query = trimmed.toLocaleLowerCase();
      return source.pipe(
        map((response) => ({
          items: response.items.filter(
            (site) =>
              site.name.toLocaleLowerCase().includes(query) ||
              site.address.toLocaleLowerCase().includes(query),
          ),
        })),
      );
    }

    return this.getDealerSiteLocations(trimmed);
  }

  getSiteDevices(siteId: number): Observable<DealerGetSiteDevicesResponse> {
    if (this.apiConfig.useMocks()) {
      return of({ items: [] });
    }
    return this.http
      .get<DealerGetSiteDevicesResponse>(this.dealerUrl(`/sites/${siteId}/devices`))
      .pipe(
        tap((response) => {
          if (!this.apiConfig.isProduction()) {
            console.info(`[Dealer] GET /sites/${siteId}/devices`, response);
          }
        }),
      );
  }

  getActivityTypes(): Observable<DealerGetActivityTypesResponse> {
    if (this.apiConfig.useMocks()) {
      return of({ items: [] });
    }
    return this.http.get<DealerGetActivityTypesResponse>(
      this.dealerUrl('/activities/types'),
    );
  }

  getTypeValues(names?: string): Observable<DealerGetTypeValuesResponse> {
    if (this.apiConfig.useMocks()) {
      return of({ items: [] });
    }
    const params = names?.trim() ? { Names: names.trim() } : undefined;
    return this.http.get<DealerGetTypeValuesResponse>(this.dealerUrl('/types'), {
      params,
    });
  }

  getFlaggedEventAlarms(eventId: string): Observable<DealerGetFlaggedEventAlarmsResponse> {
    if (this.apiConfig.useMocks()) {
      return of({ items: [] });
    }
    return this.http
      .get<DealerGetFlaggedEventAlarmsResponse>(
        this.dealerUrl(`/events/${eventId}/alarms/flagged`),
      )
      .pipe(map((response) => ({ items: response.items ?? [] })));
  }

  getArmedSites(): Observable<SiteLocationsResponse> {
    return this.http
      .get<Parameters<typeof mapDealerSiteList>[0]>(this.dealerUrl(API_ENDPOINTS.siteArmed.apiPath))
      .pipe(map((res) => mapDealerSiteList(res, true)));
  }

  getDisarmedSites(): Observable<SiteLocationsResponse> {
    return this.http
      .get<Parameters<typeof mapDealerSiteList>[0]>(
        this.dealerUrl(API_ENDPOINTS.siteDisarmed.apiPath),
      )
      .pipe(map((res) => mapDealerSiteList(res, false)));
  }

  getSiteDisarmReasons(): Observable<SiteDisarmReasonsResponse> {
    return this.getTypeValues('DisarmSiteReason').pipe(
      map((response) => mapDealerTypeValuesToDisarmReasons(response.items)),
    );
  }

  /** GET /api/dealer/types?Names=PauseReason — camera disarm reason options. */
  getCameraDisarmReasons(): Observable<SiteDisarmReasonsResponse> {
    return this.getTypeValues('PauseReason').pipe(
      map((response) => mapDealerTypeValuesToDisarmReasons(response.items)),
    );
  }

  /** POST /api/dealer/sites/{siteId}/disarm — Dealer DisarmSiteRequest (`disarmTypeId` 0 / 1 / 2). */
  disarmSite(siteId: string, body: DisarmSiteRequest): Observable<DisarmSiteResponse> {
    return this.http
      .post<void>(this.dealerUrl(`/sites/${siteId}/disarm`), mapDisarmSiteRequestToDealer(body))
      .pipe(
        map(
          (): DisarmSiteResponse => ({
            success: true,
            siteId,
            armed: false,
          }),
        ),
      );
  }

  /** Dealer site record — used for IANA timezone (live site clock). */
  getDealerSite(
    siteId: number,
  ): Observable<{ siteId: number; siteName?: string; siteAddress?: string; timezone?: string | null }> {
    return this.http.get<{
      siteId: number;
      siteName?: string;
      siteAddress?: string;
      timezone?: string | null;
    }>(this.dealerUrl(`/sites/${siteId}`));
  }

  /** Artificial loading delay used to make mock skeletons visible; no delay on a real gateway. */
  getEventsMockSettings(): Observable<EventsMockSettings> {
    if (!this.apiConfig.mockApiAvailable()) {
      return of({ loadingDelayMs: 0 });
    }
    return this.http.get<EventsMockSettings>(API_ENDPOINTS.eventsMockSettings.apiPath);
  }

  getEventDetail(eventId: string): Observable<EventDetail> {
    return this.http
      .get<Parameters<typeof mapEventDetail>[0]>(this.dealerUrl(`/events/${eventId}`))
      .pipe(
      switchMap((dto) => {
        const detail = mapEventDetail(dto);
        if (detail.address) {
          return of(detail);
        }
        return this.getDealerSite(detail.siteId).pipe(
          map((site) => ({
            ...detail,
            location: site.siteName ?? detail.location,
            address: site.siteAddress ?? detail.address,
          })),
          catchError(() => of(detail)),
        );
      }),
    );
  }

  /**
   * GET /events/{eventId}/alarms, plus flagged state.
   * Omit `alarmStatus` on the first load so every alarm comes back.
   * A periodic refresh passes `alarmStatus: 'New'` and `mergeInto` (the list
   * already loaded). New ids are appended; alarms missing from that payload stay.
   */
  getEventAlarms(
    eventId: string,
    options?: {
      alarmStatus?: DealerAlarmStatus;
      mergeInto?: EventAlarmsResponse;
    },
  ): Observable<EventAlarmsResponse> {
    const base = this.dealerUrl(`/events/${eventId}`);
    const alarmParams = options?.alarmStatus ? { alarmStatus: options.alarmStatus } : undefined;
    return forkJoin({
      alarms: this.http.get<DealerGetEventAlarmsResponse>(
        `${base}/alarms`,
        alarmParams ? { params: alarmParams } : undefined,
      ),
      flags: this.http.get<DealerGetFlaggedEventAlarmsResponse>(`${base}/alarms/flagged`).pipe(
        map((response) => ({ items: response.items ?? [], failed: false })),
        catchError(() => of({ items: [] as DealerGetFlaggedEventAlarmsResponse['items'], failed: true })),
      ),
    }).pipe(
      map(({ alarms, flags }) => {
        const incoming = mapDealerAlarmsToEventAlarms(alarms.items).items;
        const combined = options?.mergeInto
          ? mergeNewEventAlarms(options.mergeInto.items, incoming)
          : incoming;
        if (flags.failed) {
          return { items: combined, flaggedLoadFailed: true as const };
        }
        return { items: replaceFlaggedStateOnAlarms(combined, flags.items) };
      }),
    );
  }

  getEventCameraAlarms(eventId: string, cameraId: string): Observable<EventAlarmsResponse> {
    return this.http
      .get<Parameters<typeof mapAlarms>[0]>(
        this.dealerUrl(`/events/${eventId}/cameras/${cameraId}/alarms`),
      )
      .pipe(map(mapAlarms));
  }

  /** Dealer has no per-event camera list; cameras are the site's camera devices. */
  getEventCameras(siteId: number | null | undefined): Observable<EventCamerasResponse> {
    if (!siteId) {
      return of({ items: [] });
    }
    return this.siteDevices(siteId).pipe(map(mapDealerDevicesToCameras));
  }

  /** POST /api/dealer/cameras/{cameraId}/disarm — same DisarmCameraRequest shape as site disarm. */
  disarmCamera(cameraId: string, body: DisarmCameraRequest): Observable<DisarmCameraResponse> {
    const id = cameraId.trim();
    if (!id) {
      throw new Error('Invalid camera id');
    }

    return this.http
      .post<void>(this.dealerUrl(`/cameras/${id}/disarm`), mapDisarmSiteRequestToDealer(body))
      .pipe(
        map(
          (): DisarmCameraResponse => ({
            success: true,
            cameraId: id,
          }),
        ),
      );
  }

  /** Dealer has no per-event talkdown list; speakers are the site's speaker devices. */
  getEventTalkdowns(siteId: number | null | undefined): Observable<EventTalkdownsResponse> {
    if (!siteId) {
      return of({ items: [] });
    }
    return this.siteDevices(siteId).pipe(map(mapDealerDevicesToTalkdowns));
  }

  activateTalkdown(body: DealerActivateTalkdownRequest): Observable<string> {
    if (this.apiConfig.useMocks()) {
      return of('ok');
    }
    return this.http.post(
      this.dealerUrl('/talkdown/activate'),
      body,
      { responseType: 'text' },
    );
  }

  /** Saves an EventQ talkdown result for the Resolve Incident Report in this browser. */
  recordTalkdownAttempt(
    eventId: string,
    body: { talkdownId: number; succeeded: boolean },
  ): Observable<{ talkdownId: number; succeeded: boolean }> {
    appendTalkdownAttempt(eventId, body);
    return of(body);
  }

  flagEventAlarms(
    eventId: string,
    flags: DealerFlagEventAlarm[],
  ): Observable<DealerFlagEventAlarmsResponse> {
    const usable = flags.filter((flag) => flag.alarmId.trim().length > 0);
    if (usable.length === 0) {
      return of({ items: [] });
    }
    if (this.apiConfig.useMocks()) {
      return of({ items: [] });
    }
    return this.http.post<DealerFlagEventAlarmsResponse>(
      this.dealerUrl(`/events/${eventId}/alarms/flag`),
      { flags: usable },
    );
  }

  private siteDevices(siteId: number): Observable<DealerDevice[]> {
    return this.getSiteDevices(siteId).pipe(map((response) => response.items ?? []));
  }

  private getDealerSiteLocations(search?: string): Observable<SiteLocationsResponse> {
    const trimmed = search?.trim();

    return forkJoin({
      allowed: this.http.get<DealerGetAllowedSitesResponse>(this.dealerUrl('/routing/allowed-sites')),
      armed: this.http.get<DealerGetArmedSitesResponse>(this.dealerUrl('/sites/armed')),
      disarmed: this.http.get<DealerGetDisarmedSitesResponse>(this.dealerUrl('/sites/disarmed')),
    }).pipe(
      switchMap(({ allowed, armed, disarmed }) => {
        const allowedIds = resolveDealerAllowedSiteIds(
          allowed.allowedSites,
          this.apiConfig.dealerBypassAllowedSitesFilter(),
        );
        const armedIds = new Set((armed.items ?? []).map((site) => Number(site.siteId)));

        if (!trimmed) {
          return of(mapDealerSiteSummariesToLocations(armed.items, disarmed.items, allowedIds));
        }

        return this.http
          .get<DealerPaginationResponseOfSiteItem>(this.dealerUrl('/sites/search'), {
            params: {
              MaxResultCount: '50',
              Search: trimmed,
            },
          })
          .pipe(
            map((response) =>
              mapDealerSitesToLocations(response.items ?? [], allowedIds, armedIds),
            ),
          );
      }),
    );
  }

  getRaiseEventOutcomes(): Observable<EventOutcomesResponse> {
    return this.activityTypes().pipe(map(mapDealerActivityTypesToRaiseOutcomes));
  }

  getCloseEventOutcomes(): Observable<EventOutcomesResponse> {
    return this.activityTypes().pipe(map(mapDealerActivityTypesToCloseOutcomes));
  }

  /** Single catalog behind both outcome pickers — Dealer has no per-action list. */
  private activityTypes(): Observable<DealerActivityType[] | null> {
    return this.http
      .get<DealerGetActivityTypesResponse>(this.dealerUrl('/activities/types'))
      .pipe(map((response) => response?.items ?? null));
  }

  /** GET Resolve `/events/escalated` — unclaimed raised events. */
  getResolveEscalatedEvents(): Observable<ResolveEventsResponse> {
    return this.getResolveEventList(API_ENDPOINTS.resolveEventsEscalated.apiPath);
  }

  /** GET Resolve `/events/in-progress` — claimed escalated events. */
  getResolveInProgressEvents(): Observable<ResolveEventsResponse> {
    return this.getResolveEventList(API_ENDPOINTS.resolveEventsInProgress.apiPath);
  }

  /** GET Resolve `/events/parked` — parked escalated events. */
  getResolveParkedEvents(): Observable<ResolveEventsResponse> {
    return this.getResolveEventList(API_ENDPOINTS.resolveEventsParked.apiPath);
  }

  private getResolveEventList(path: string): Observable<ResolveEventsResponse> {
    return this.http
      .get<ResolveGetEventsResponse>(this.resolveUrl(path))
      .pipe(map((response) => ({ items: response.items ?? [] })));
  }

  /** Escalation lives on the Resolve API — Dealer has no raise operation. */
  raiseEventToResolve(
    eventId: string,
    body: RaiseToResolveRequest,
  ): Observable<EventActionResponse> {
    return this.http
      .post<DealerEscalateEventResponse>(
        this.resolveUrl('/events/escalate'),
        { eventId, escalatedActivityTypeId: activityTypeId(body.outcomeId) },
      )
      .pipe(
        map((response) => ({
          success: true,
          eventId: response?.event?.eventId ?? eventId,
        })),
      );
  }

  closeEvent(eventId: string, body: CloseEventRequest): Observable<EventActionResponse> {
    return this.http
      .patch<DealerCloseEventResponse>(
        this.dealerUrl(`/events/${eventId}/close`),
        {
          closedActivityTypeId: activityTypeId(body.outcomeId),
          closingDetails: body.closingDetails,
        },
      )
      .pipe(map((response) => ({ success: true, eventId: response?.eventId ?? eventId })));
  }

  getEventsParked(sort?: EventqSortParams): Observable<EventsParkedResponse> {
    return this.http
      .get<DealerGetParkedEventsResponse>(this.dealerUrl('/events/parked'), {
        params: this.sortParams(sort),
      })
      .pipe(map((response) => mapDealerParkedEventsToResponse(response.items)));
  }

  parkEvent(
    eventId: string,
    body: ParkEventRequest & {
      location?: string;
      patrolStarted?: string;
      parkedBy?: string;
    },
  ): Observable<ParkEventResponse> {
    return this.http
      .post<DealerParkEventResponse>(this.dealerUrl(`/events/${eventId}/park`), {
        durationMinutes: body.durationMinutes,
      })
      .pipe(map((response) => mapDealerParkEventToResponse(response.event, body.durationMinutes)));
  }

  /**
   * GET Resolve `/sites/{siteId}/event-history`, then Dealer `/events/{eventId}/alarms`
   * for each event. Maps the matching camera onto 10s timeline blocks.
   */
  getHistoricPlaybackAlarms(
    siteId: number,
    cameraNumericId: number,
    extraEventIds: readonly string[] = [],
  ): Observable<HistoricPlaybackAlarmsResult> {
    const id = requirePositiveSiteId(siteId);
    const extraIds = extraEventIds.map((eventId) => eventId.trim()).filter((eventId) => eventId.length);
    return this.http
      .get<ResolveGetSiteEventHistoryResponse>(this.resolveUrl(`/sites/${id}/event-history`))
      .pipe(
        tap((response) => {
          if (!this.apiConfig.isProduction()) {
            console.info(`[Resolve] GET /sites/${id}/event-history`, response);
          }
        }),
        map((response) => mergeHistoricEventIds(mapSiteEventHistoryToEventIds(response), extraIds)),
        catchError(() => of(mergeHistoricEventIds([], extraIds))),
        switchMap((eventIds) => {
          if (!eventIds.length) {
            return of({ alarms: [], segments: emptyHistoricPlaybackAlarmSegments() });
          }
          return forkJoin(
            eventIds.map((eventId) =>
              this.http
                .get<DealerGetEventAlarmsResponse>(this.dealerUrl(`/events/${eventId}/alarms`))
                .pipe(
                  map((response) => mapDealerAlarmsToEventAlarms(response.items).items),
                  catchError(() => of([])),
                ),
            ),
          ).pipe(
            map((alarmLists) => {
              const alarms = alarmLists.flat();
              const segments = mapHistoricAlarmsToTimelineSegments(alarms, cameraNumericId);
              if (!this.apiConfig.isProduction()) {
                console.info('[Dealer] historic playback alarms', {
                  siteId: id,
                  cameraNumericId,
                  eventIds,
                  segments,
                });
              }
              return { alarms, segments };
            }),
          );
        }),
      );
  }

  /** GET /sites/{siteId}/contacts — Resolve contract (no UI consumer yet). */
  getSiteContacts(siteId: number): Observable<ResolveSiteContactsResponse> {
    const id = requirePositiveSiteId(siteId);
    return this.http
      .get<ResolveGetSiteContactsResponse>(this.resolveUrl(`/sites/${id}/contacts`))
      .pipe(map((response) => ({ items: response.items ?? [] })));
  }

  /** GET /cameras/{cameraId}/alarm-history — Resolve contract (no UI consumer yet). */
  getCameraAlarmHistory(
    cameraId: number,
    startDateTime: string,
    endDateTime: string,
  ): Observable<ResolveCameraAlarmHistoryResponse> {
    const id = requirePositiveCameraId(cameraId);
    const start = requireIsoDateTime(startDateTime, 'StartDateTime');
    const end = requireIsoDateTime(endDateTime, 'EndDateTime');
    return this.http
      .get<ResolveGetCameraAlarmHistoryResponse>(this.resolveUrl(`/cameras/${id}/alarm-history`), {
        params: { StartDateTime: start, EndDateTime: end },
      })
      .pipe(map((response) => ({ items: response.items ?? [] })));
  }

  /** POST /events/create-incident — Resolve contract (OpenAPI lists no 200 schema). */
  createIncident(eventId: string): Observable<ResolveCreateIncidentResponse> {
    const id = requireNonBlankEventId(eventId);
    return this.http
      .post(this.resolveUrl(API_ENDPOINTS.resolveCreateIncident.apiPath), { eventId: id })
      .pipe(map(() => ({ success: true as const })));
  }

  /**
   * POST /api/dealer/events/:eventId/playback/clip — returns a downloadable video blob.
   * Filename comes from `Content-Disposition` when present.
   */
  exportPlaybackClip(
    eventId: string,
    body: EventPlaybackClipRequest,
  ): Observable<EventPlaybackClipDownload> {
    return this.http
      .post(this.dealerUrl(`/events/${eventId}/playback/clip`), body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const blob = response.body ?? new Blob();
          const filename =
            parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
            buildPlaybackClipFilename(body);
          return { blob, filename };
        }),
      );
  }

  getEventCameraStream(
    eventId: string,
    numericId: number,
    streamType: 'main' | 'sub' = 'main',
  ): Observable<EventCameraStreamResponse> {
    if (!this.apiConfig.mockApiAvailable()) {
      return throwError(
        () => new Error('Camera streaming is not available outside mock mode.'),
      );
    }
    return this.http.get<EventCameraStreamResponse>(
      this.dealerUrl(`/events/${eventId}/cameras/${numericId}/stream`),
      { params: { streamType } },
    );
  }

  getCameraDetections(numericId: number): Observable<CameraDetectionsResponse> {
    if (!this.apiConfig.mockApiAvailable()) {
      return of({ cameraNumericId: numericId, at: null, boxes: [] });
    }
    return this.http.get<CameraDetectionsResponse>(
      this.dealerUrl(`/cameras/${numericId}/detections`),
    );
  }

  /** POST Scalar `/cameras/{cameraId}/ptz/zoom` or `/ptz/center`. Empty 200 maps to `{ ok: true }`. */
  sendCameraPtz(numericId: number, body: CameraPtzRequest): Observable<CameraPtzResponse> {
    const cameraId = requirePositiveCameraId(numericId);
    const command = mapCameraPtzActionToDealer(body.action, body.frame);
    if (!command) {
      throw new Error('Unsupported PTZ action');
    }

    return this.http
      .post<void>(this.dealerUrl(`/cameras/${cameraId}/ptz/${command.path}`), command.body)
      .pipe(map(() => ({ ok: true })));
  }

  submitCameraReport(
    eventId: string,
    cameraId: string,
    body: CameraReportRequest,
    site?: { siteId: number; siteName: string },
  ): Observable<CameraReportResponse> {
    if (!this.apiConfig.useMocks()) {
      return this.createDealerCase(body, site);
    }
    const { nvrId: _nvrId, nvrName: _nvrName, ...camera } = body.camera;
    return this.http.post<CameraReportResponse>(
      this.dealerUrl(`/events/${eventId}/cameras/${cameraId}/report`),
      { ...body, camera },
    );
  }

  private createDealerCase(
    body: CameraReportRequest,
    site?: { siteId: number; siteName: string },
  ): Observable<CameraReportResponse> {
    if (!site || site.siteId <= 0 || !site.siteName.trim()) {
      return throwError(() => new Error('Dealer case requires siteId and siteName'));
    }

    return this.http
      .post<DealerCreateCaseResponse>(this.dealerUrl('/case'), {
        siteId: site.siteId,
        siteName: site.siteName.trim(),
        cameraId: body.camera.numericId,
        cameraName: body.camera.name,
        caseName: mapCameraReportReasonToDealerCaseName(body.report.reasonId),
        nvrId: body.camera.nvrId ?? null,
        nvrName: body.camera.nvrName ?? null,
      })
      .pipe(
        map((response) => ({
          success: response.success,
          message: response.responseMessage,
          reportId: response.id,
        })),
      );
  }

  /**
   * POST `{resolveApiBaseUrl}/events/{eventId}/assets`
   * Snapshot → `assetTypeId` 0 + `image`; attach-clip → `assetTypeId` 1 + `clip`.
   */
  captureSnapshot(eventId: string, body: SnapshotRequest): Observable<SnapshotResponse> {
    return this.http
      .post<ResolveSaveEventAssetResponse>(this.resolveUrl(`/events/${eventId}/assets`), body)
      .pipe(map(mapResolveAssetToSnapshotResponse));
  }

  /** POST `{resolveApiBaseUrl}/events/{eventId}/assets` — attach clip (`assetTypeId` 1). */
  attachAlarmClip(
    eventId: string,
    body: AttachAlarmClipRequest,
  ): Observable<AttachAlarmClipResponse> {
    return this.http
      .post<ResolveSaveEventAssetResponse>(this.resolveUrl(`/events/${eventId}/assets`), body)
      .pipe(map(mapResolveAssetToClipResponse));
  }

  /**
   * POST `{doriApiBaseUrl}/flag_inference` — operator false-positive AI feedback.
   * Local/EBM uses same-origin `/api/dori` (no browser-bundled secret).
   * Absolute external Dori hosts require a runtime `doriApiToken` (never commit).
   * Stealth JWT is never attached (auth interceptor skips `/dori`).
   */
  submitAiFeedback(
    eventId: string,
    _detectionId: string,
    body: AiFeedbackRequest,
  ): Observable<AiFeedbackResponse> {
    const base = (this.apiConfig.doriApiBaseUrl() || '/api/dori').replace(/\/$/, '');
    const token = this.apiConfig.doriApiToken()?.trim() ?? '';
    const isAbsolute = /^https?:\/\//i.test(base);
    if (isAbsolute && !token) {
      return throwError(() => new DoriAuthConfigError());
    }

    const payload: DoriFlagInferenceRequest = {
      event_id: eventId,
      annotation: { reason: doriAnnotationReasonFromVariant(body.feedback.variant) },
    };

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http
      .post<DoriFlagInferenceResponse>(`${base}/flag_inference`, payload, { headers })
      .pipe(
        map((response) => ({
          success: true,
          flagId: response.flag_id,
        })),
      );
  }

  /**
   * Builds a Dealer URL from the runtime base. Registry paths that start with
   * `/api/dealer` keep that prefix only as documentation — the live base wins.
   */
  private dealerUrl(path: string): string {
    const registeredPrefix = '/api/dealer';
    const suffix = path.startsWith(registeredPrefix) ? path.slice(registeredPrefix.length) : path;
    return `${this.apiConfig.dealerApiBaseUrl()}${suffix.startsWith('/') ? suffix : `/${suffix}`}`;
  }

  /**
   * Builds a Resolve URL from the runtime base. Registry paths that start with
   * `/api/resolve` keep that prefix only as documentation — the live base wins.
   */
  private resolveUrl(path: string): string {
    const registeredPrefix = '/api/resolve';
    const suffix = path.startsWith(registeredPrefix) ? path.slice(registeredPrefix.length) : path;
    return `${this.apiConfig.resolveApiBaseUrl()}${suffix.startsWith('/') ? suffix : `/${suffix}`}`;
  }

  private sortParams(sort?: EventqSortParams): Record<string, string> {
    if (!sort?.column) {
      return {};
    }

    return {
      [EVENTQ_SORT_QUERY_KEYS.column]: sort.column,
      [EVENTQ_SORT_QUERY_KEYS.direction]: sort.direction ?? 'asc',
    };
  }
}

